import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBriefcase, faGlobe, faUserGraduate, faHandshake } from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';

const Career: React.FC = () => {
  // Set page title
  useEffect(() => {
    document.title = "Career Opportunities | Skill Point Consultants";
  }, []);

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1, 
      transition: { 
        duration: 0.4, 
        ease: "easeOut" 
      } 
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 15 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        duration: 0.4, 
        ease: [0.1, 0.4, 0.2, 1]
      } 
    }
  };

  const staggerContainer = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.05
      }
    }
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-r from-primary/10 to-secondary/10">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="section-title text-4xl md:text-5xl font-bold text-gray-800"
            >
              Career Opportunities
            </motion.h1>
            <motion.p
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              className="text-lg md:text-xl text-gray-700 mt-8"
            >
              Join our network of professionals and discover new pathways to success
            </motion.p>
          </div>
        </div>
      </section>

      {/* International Jobs Section */}
      <section className="section py-16">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="max-w-4xl mx-auto"
          >
            <motion.div variants={fadeInUp} className="mb-12">
              <div className="flex items-center mb-6">
                <div className="bg-primary/10 p-3 rounded-full mr-4">
                  <FontAwesomeIcon icon={faGlobe} className="text-primary text-2xl" />
                </div>
                <h2 className="text-3xl font-bold text-gray-800">Jobs Available in Malaysia and South Korea</h2>
              </div>
              <p className="text-lg text-gray-700 mb-6">
                Looking for exciting career opportunities abroad? We bring you the best job openings 
                in Malaysia and South Korea across multiple sectors!
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-10 bg-gray-50 p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Available Job Categories:</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span><strong>Hotel Industry:</strong> Housekeeping, Front Office, F&B Service, Kitchen Staff, and more.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span><strong>Construction & Warehouse:</strong> Skilled and Unskilled Workers, Helpers, Forklift Operators, Supervisors.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-2">•</span>
                  <span><strong>Farming Jobs:</strong> Fruit & Vegetable Picking, Greenhouse Farming, Dairy Farming, and General Farm Workers.</span>
                </li>
              </ul>
            </motion.div>

            <motion.div variants={fadeInUp} className="mb-12">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Destinations:</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-lg shadow border border-gray-100">
                  <h4 className="font-semibold text-lg mb-2">Malaysia</h4>
                  <p className="text-gray-700">A hub for hospitality and construction jobs with excellent growth opportunities.</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow border border-gray-100">
                  <h4 className="font-semibold text-lg mb-2">South Korea</h4>
                  <p className="text-gray-700">Famous for high-paying farming and warehouse jobs with great living standards.</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Local Opportunities Section */}
      <section className="section py-16 bg-gray-50">
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="max-w-4xl mx-auto"
          >
            <motion.div variants={fadeInUp} className="mb-12">
              <div className="flex items-center mb-6">
                <div className="bg-secondary/10 p-3 rounded-full mr-4">
                  <FontAwesomeIcon icon={faBriefcase} className="text-secondary text-2xl" />
                </div>
                <h2 className="text-3xl font-bold text-gray-800">Local Opportunities</h2>
              </div>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              <motion.div variants={fadeInUp} className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center mb-4">
                  <FontAwesomeIcon icon={faHandshake} className="text-primary mr-3 text-xl" />
                  <h3 className="text-xl font-semibold">Freelancer (HR Projects)</h3>
                </div>
                <ul className="space-y-2 mb-4 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Work remotely on flexible assignments</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Ideal for experienced professionals or consultants</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>We also welcome candidates with career gaps who are eager to restart their professional journey</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Competitive project-based compensation</span>
                  </li>
                </ul>
              </motion.div>

              <motion.div variants={fadeInUp} className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center mb-4">
                  <FontAwesomeIcon icon={faUserGraduate} className="text-secondary mr-3 text-xl" />
                  <h3 className="text-xl font-semibold">Internship Opportunities (HR Department)</h3>
                </div>
                <ul className="space-y-2 mb-4 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Paid and Unpaid internships available</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Duration: 3–6 months</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Open to freshers, students, and career switchers</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Gain hands-on experience in recruitment, employee engagement, training, and more</span>
                  </li>
                </ul>
              </motion.div>
            </div>

            <motion.div variants={fadeInUp} className="mt-12 bg-white p-6 rounded-lg shadow">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">Pathway to Full-Time Employment</h3>
              <p className="mb-4 text-gray-700">
                Interns and freelancers who successfully complete assessments and meet performance standards 
                will be offered full-time positions at Skill Point Consultants.
              </p>
              <p className="text-gray-700">
                Grow with us and become a part of a dynamic HR team shaping future talent.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* How to Apply Section - CTA */}
      <section className="section py-20 bg-gradient-to-r from-[#B01C1C] via-[#483D8B] to-[#00A7D1] text-white">
        <div className="container text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="max-w-3xl mx-auto"
          >
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold mb-6 text-white">
              How to Apply
            </motion.h2>
            <motion.p variants={fadeIn} className="text-lg text-white/90 mb-10">
              If you are passionate about HR and ready to make a difference, we would love to hear from you!
            </motion.p>
            <motion.div variants={fadeInUp} className="bg-white p-6 rounded-lg shadow inline-block">
              <a 
                href="https://wa.me/918295531760?text=Hello%2C%20I%20would%20like%20to%20send%20my%20updated%20resume%20for%20career%20opportunities." 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 py-3 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                Send your updated resume to +91-8295531760
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Career; 